import { Send } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { SaveAndResumeLater, type SaveDraftResult } from "../../components/forms/SaveAndResumeLater";
import { buildSubmissionSummary, type SubmissionSummary } from "../../lib/formSubmissionSummary";
import {
  appendStoredDraftFiles,
  buildResumeUrl,
  getCurrentSectionId,
  getResumeDraftId,
  loadFormProgress,
  restoreFormProgress,
  saveFormProgress,
  type DraftFileMeta,
} from "../../lib/formProgress";
import {
  CheckboxField,
  CheckboxGroupField,
  FileField,
  FormSection,
  RadioGroupField,
  SelectField,
  TextareaField,
  TextField,
  ThankYouState,
  TurnstilePlaceholder,
  printSubmittedForm,
} from "./FormPageShared";
import { FormPortalHeader } from "./FormPortalHeader";
import type { ConfiguredField, ConfiguredServiceForm } from "./configuredServiceForms";

type FormErrors = Record<string, string>;

export function ConfiguredServiceFormPage({ config }: { config: ConfiguredServiceForm }) {
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<SubmissionSummary | null>(null);
  const [draftId, setDraftId] = useState("");
  const [restoreNotice, setRestoreNotice] = useState("");
  const [restoredFiles, setRestoredFiles] = useState<DraftFileMeta[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.title = `${config.formTitle} | Kreatethedate`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", config.metaDescription);
  }, [config.formTitle, config.metaDescription]);

  useEffect(() => {
    const resumeDraftId = getResumeDraftId();
    if (!resumeDraftId) {
      const targetSectionId = window.location.hash.replace("#", "");
      if (targetSectionId) {
        window.setTimeout(() => {
          document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
        }, 80);
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
      return;
    }

    const draft = loadFormProgress(config.formKey, resumeDraftId);
    if (!draft || !formRef.current) {
      setRestoreNotice("We could not find saved progress for this resume link in this browser.");
      return;
    }

    restoreFormProgress(formRef.current, draft);
    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    setRestoreNotice("Your saved progress has been restored in this browser.");

    const targetSectionId = window.location.hash.replace("#", "") || draft.lastSectionId || config.sections[0]?.id;
    window.setTimeout(() => {
      if (formRef.current) restoreFormProgress(formRef.current, draft);
      document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
    }, 80);
  }, [config.formKey, config.sections]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("formType", config.formKey);
    formData.set("cf-turnstile-response", "turnstile-placeholder");
    if (draftId) await appendStoredDraftFiles(formData, config.formKey, draftId);

    const nextErrors = validateConfiguredForm(config, formData);
    setErrors(nextErrors);

    const firstInvalidName = Object.keys(nextErrors)[0];
    if (firstInvalidName) {
      setSubmitError(config.requiredMessage);
      form.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)?.focus();
      return;
    }

    const summary = buildSubmissionSummary({
      formData,
      formTitle: config.setupTitle,
      clientNameField: clientNameFieldFor(config),
      eventNameField: config.eventNameField,
      eventDateField: config.eventDateField,
      eventNameLabel: config.eventNameLabel || (config.formKey === "partner-referral" ? "Referral name" : undefined),
      eventDateLabel: config.eventDateLabel,
      sections: config.summarySections,
    });

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/forms/${config.formKey}`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const message = await response.text();
        if (!(import.meta.env.DEV && response.status === 404)) {
          throw new Error(message || "Your form could not be submitted. Please try again.");
        }
      }

      const contentType = response.headers.get("Content-Type") || "";
      if (response.ok && !contentType.includes("application/json") && !import.meta.env.DEV) {
        throw new Error(`The ${config.formTitle} submission endpoint is not configured yet.`);
      }

      setSubmittedSummary(summary);
      setSubmitted(true);
      formRef.current?.reset();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Your form could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSaveProgress(): Promise<SaveDraftResult> {
    if (!formRef.current) throw new Error("The form is not ready yet.");

    const lastSectionId = getCurrentSectionId(config.sections.map((section) => section.id));
    const draft = await saveFormProgress({
      form: formRef.current,
      formKey: config.formKey,
      draftId,
      lastSectionId,
    });
    const resumeLink = buildResumeUrl(config.formPath, draft.draftId, lastSectionId);

    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    return { draft, resumeLink };
  }

  return (
    <main className="admin-form-page min-h-screen bg-[#fff9ef] text-[#16120a]">
      <a
        href={`#${config.formKey}-form`}
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#050505] focus:px-4 focus:py-3 focus:text-[#FF6A2B]"
      >
        Skip to {config.formTitle}
      </a>
      <FormPortalHeader />

      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18">
        <img src="/assets/celebration-table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,.94),rgba(5,5,5,.74)_54%,rgba(5,5,5,.48)),linear-gradient(0deg,rgba(5,5,5,.42),rgba(5,5,5,.14))]" />
        <div className="relative mx-auto grid min-h-[46dvh] max-w-7xl items-end gap-8 px-4 pb-10 pt-24 text-[#fff9ef] sm:px-6 lg:grid-cols-[1fr_.72fr] lg:px-8 lg:pb-14">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6A2B]">{config.eyebrow}</p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              {config.heroHeading}
            </h1>
          </div>
          <p className="max-w-xl border-l-2 border-[#FF6A2B] pl-4 text-sm font-semibold leading-7 text-[#fff9ef]/76 lg:ml-auto">
            {config.heroSubheading}
          </p>
        </div>
      </section>

      {submitted ? (
        <ThankYouState
          message={config.thankYouMessage}
          onDownloadPdf={() => printSubmittedForm(submittedSummary)}
          onPrint={() => printSubmittedForm(submittedSummary)}
          summary={submittedSummary}
        />
      ) : (
        <section className="px-4 pb-28 pt-10 sm:px-6 lg:px-8 lg:pb-28 lg:pt-14">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[290px_1fr]">
            <aside className="hidden h-fit rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] lg:sticky lg:top-24 lg:block">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Form sections</p>
              <ol className="mt-4 grid gap-2">
                {config.sections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="focus-ring group grid grid-cols-[34px_1fr] items-center gap-3 rounded-lg px-2 py-2 text-sm font-bold text-[#5c5246] transition hover:bg-[#fff9ef] hover:text-[#050505]"
                    >
                      <span className="grid size-8 place-items-center rounded-full border border-[#FF6A2B]/45 bg-[#050505] text-xs text-[#FF6A2B]">
                        {index + 1}
                      </span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <form id={`${config.formKey}-form`} ref={formRef} noValidate onSubmit={handleSubmit} className="grid gap-6">
              <SaveAndResumeLater
                formTitle={config.formTitle}
                getDefaultEmail={() => {
                  const emailControl = formRef.current?.elements.namedItem(saveEmailFieldFor(config));
                  return emailControl instanceof HTMLInputElement ? emailControl.value : "";
                }}
                onSaveDraft={handleSaveProgress}
                restoreNotice={restoreNotice}
                savedFiles={restoredFiles}
              />
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Estimated completion time: 10&ndash;15 minutes. You can save your progress and return at any time.
              </p>
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Required fields are marked with <span className="font-black text-[#D94A16]">*</span>.
              </p>
              {submitError ? (
                <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800" role="alert">
                  {submitError}
                </p>
              ) : null}

              {config.sections.map((section, index) => (
                <FormSection
                  key={section.id}
                  id={section.id}
                  number={String(index + 1).padStart(2, "0")}
                  title={section.title}
                  formName={config.formTitle}
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    {section.fields.map((field, fieldIndex) => (
                      <ConfiguredFieldRenderer
                        key={`${section.id}-${field.name || fieldIndex}`}
                        error={field.name ? errors[field.name] : undefined}
                        field={field}
                      />
                    ))}
                    {section.id === "confirmation" ? <TurnstilePlaceholder /> : null}
                  </div>
                </FormSection>
              ))}

              <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                <div className="grid gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{config.formTitle}</p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-[#050505]">Change Request Notice</h2>
                  </div>
                  <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    <p>Please review your information carefully before submitting.</p>
                    <p>
                      Kreatethedate will use the information provided to prepare your requested service. Major changes
                      requested after setup has begun may affect the project timeline and may require additional work,
                      revisions, or fees.
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex flex-col gap-3 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  Please review your details before submitting your {config.formTitle.toLowerCase()}.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-6 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : config.submitLabel}
                  <Send className="size-4" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function ConfiguredFieldRenderer({ field, error }: { field: ConfiguredField; error?: string }) {
  if (field.type === "notice") {
    return (
      <div className={field.className || "md:col-span-2"}>
        <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {(field.content || []).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    );
  }

  if (!field.name || !field.label) return null;

  if (field.type === "textarea") {
    return (
      <TextareaField
        className={field.className || "md:col-span-2"}
        error={error}
        helperText={field.helperText}
        name={field.name}
        label={field.label}
        required={field.required}
        rows={field.rows || 5}
      />
    );
  }

  if (field.type === "select") {
    return (
      <SelectField
        className={field.className}
        error={error}
        name={field.name}
        label={field.label}
        options={field.options || []}
        required={field.required}
      />
    );
  }

  if (field.type === "radio") {
    return (
      <RadioGroupField
        className={field.className || "md:col-span-2"}
        error={error}
        helperText={field.helperText}
        name={field.name}
        label={field.label}
        options={field.options || []}
        required={field.required}
      />
    );
  }

  if (field.type === "checkboxGroup") {
    return (
      <CheckboxGroupField
        className={field.className || "md:col-span-2"}
        error={error}
        helperText={field.helperText}
        name={field.name}
        label={field.label}
        options={field.options || []}
        required={field.required}
      />
    );
  }

  if (field.type === "checkbox") {
    return <CheckboxField className={field.className || "md:col-span-2"} error={error} name={field.name} label={field.label} required={field.required} />;
  }

  if (field.type === "file") {
    return (
      <FileField
        accept={field.accept}
        className={field.className || "md:col-span-2"}
        error={error}
        helperText={field.helperText}
        multiple={field.multiple}
        name={field.name}
        label={field.label}
        required={field.required}
      />
    );
  }

  return (
    <TextField
      autoComplete={field.autoComplete}
      className={field.className}
      error={error}
      helperText={field.helperText}
      name={field.name}
      label={field.label}
      required={field.required}
      type={field.type}
    />
  );
}

function validateConfiguredForm(config: ConfiguredServiceForm, formData: FormData) {
  const nextErrors: FormErrors = {};
  const requiredFields = config.sections.flatMap((section) => section.fields).filter((field) => field.required && field.name);

  for (const field of requiredFields) {
    if (!field.name) continue;

    if (field.type === "checkbox") {
      if (!formData.get(field.name)) nextErrors[field.name] = config.requiredLabels[field.name] || `${field.label} is required.`;
      continue;
    }

    if (field.type === "checkboxGroup") {
      const hasSelection = formData
        .getAll(field.name)
        .filter((entry): entry is string => typeof entry === "string")
        .some((value) => value.trim());
      if (!hasSelection) nextErrors[field.name] = config.requiredLabels[field.name] || `${field.label} is required.`;
      continue;
    }

    const value = String(formData.get(field.name) || "").trim();
    if (!value) nextErrors[field.name] = config.requiredLabels[field.name] || `${field.label} is required.`;
  }

  const emailFields = config.sections
    .flatMap((section) => section.fields)
    .filter((field) => field.type === "email" && field.name)
    .map((field) => field.name as string);

  for (const fieldName of emailFields) {
    const email = String(formData.get(fieldName) || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors[fieldName] = "Enter a valid email address.";
    }
  }

  return nextErrors;
}

function clientNameFieldFor(config: ConfiguredServiceForm) {
  if (config.clientNameField) return config.clientNameField;
  return config.formKey === "partner-referral" ? "referrerName" : "fullName";
}

function saveEmailFieldFor(config: ConfiguredServiceForm) {
  if (config.saveEmailField) return config.saveEmailField;
  return config.formKey === "partner-referral" ? "referrerEmail" : "email";
}
